import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import HttpHelper from '../../utils/HttpHelper';
import { NavLink, useParams } from 'react-router-dom';
import FileUploadIcon from '@mui/icons-material/FileUpload';

interface MultipleChoice {
  id: number;
  option: string;
  isCorrect: boolean;
}

interface Question {
  id: number;
  text: string;
  type: 0 | 1 | 2 | 3;
  file?: string;
  maxGrade: number;
  possibleChoice?: MultipleChoice[];
}

const QUESTION_TYPES = {
  0: 'Text Input',
  1: 'File Upload',
  2: 'Multiple Choice',
  3: 'Single Choice',
};

const ViewAssessmentHR: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [questionList, setQuestionList] = useState<Question[]>([]);
  const [isLinkedToJob, setIsLinkedToJob] = useState<boolean>(false); //added state for isLinkedToJob

  const updateAssessment = () => {
    HttpHelper.get(`assessments/${assessmentId}`).then(assessmentResponse => {
      setTitle(assessmentResponse.title);
      setDescription(assessmentResponse.description);
      setIsLinkedToJob(assessmentResponse.isLinkedToJob || false); // set the state for isLinkedToJob
    });
    HttpHelper.get(`assessments/${assessmentId}/questions`).then(
      questionsResponse => {
        setQuestionList(questionsResponse);
      },
    );
  };

  useEffect(() => {
    updateAssessment();
  }, []);

  return (
    <Box
      sx={{
        width: '95%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: '24px',
        display: 'flex',
        justifySelf: 'center',
      }}
    >
      <Grid container>
        <Grid item xs={3} />
        <Grid item xs={6}>
          <Stack
            direction="column"
            spacing={4}
            mb={2}
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Card style={{ width: '100%' }}>
              <CardContent
                sx={{
                  marginTop: '24px',
                  marginBottom: '24px',
                  marginLeft: '48px',
                  marginRight: '48px',
                }}
              >
                {/* Remove the outer Typography wrapper */}
                <Typography
                  align="left"
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{
                    width: '80%',
                    fontWeight: 'bold',
                  }}
                >
                  {title}
                </Typography>
                <Divider sx={{ my: 3 }} />
                <Typography
                  variant="body1"
                  align="left"
                  gutterBottom
                  sx={{ width: '80%' }}
                >
                  {description}
                </Typography>
              </CardContent>
            </Card>
            {questionList.map((question, index) => {
              return (
                <Card style={{ width: '100%' }} key={index}>
                  <CardContent
                    sx={{
                      marginTop: '24px',
                      marginBottom: '12px',
                      marginLeft: '48px',
                      marginRight: '48px',
                    }}
                  >
                    <Typography
                      align="left"
                      variant="h5"
                      component="h3"
                      gutterBottom
                      sx={{
                        width: '80%',
                        fontWeight: 'bold',
                      }}
                    >
                      Question {index + 1} - {QUESTION_TYPES[question.type]}
                    </Typography>
                    <Typography
                      align="left"
                      variant="h6"
                      component="h4"
                      gutterBottom
                      sx={{
                        width: '80%',
                      }}
                    >
                      {question.text}
                    </Typography>
                    {QUESTION_TYPES[question.type] === 'File Upload' ? (
                      <Box
                        sx={{
                          p: 2,
                          border: '1px dashed grey',
                          width: '80%',
                        }}
                      >
                        <ListItem>
                          <ListItemIcon>
                            <FileUploadIcon />
                          </ListItemIcon>
                          <ListItemText primary="File upload" />
                        </ListItem>
                      </Box>
                    ) : null}
                    {QUESTION_TYPES[question.type] === 'Multiple Choice' || QUESTION_TYPES[question.type] === 'Single Choice' ? (
                      <Stack direction="column" spacing={1}>
                        <RadioGroup>
                          {question.possibleChoice
                            ? [...question.possibleChoice].sort((a, b) => a.id - b.id)
                              .map((choice, index) => {
                                return (
                                  <FormControlLabel
                                    key={choice.id}
                                    control={<Radio />}
                                    checked={choice.isCorrect}
                                    label={
                                      choice.isCorrect
                                        ? `${choice.option} - (correct answer)`
                                        : choice.option
                                    }
                                  />
                                );
                              })
                            : null}
                        </RadioGroup>
                      </Stack>
                    ) : null}
                    {question.file && (
                      <Typography
                        variant="body1"
                        align="left"
                        gutterBottom
                        sx={{ width: '80%', marginTop: '36px' }}
                      >
                        Attachment available (Q{index + 1}.pdf)
                      </Typography>
                    )}
                    <Typography
                      variant="body1"
                      align="left"
                      gutterBottom
                      sx={{
                        width: '80%',
                        marginTop: '36px',
                      }}
                      fontStyle="italic"
                    >
                      Maximum grade: {question.maxGrade}
                    </Typography>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Grid>
        <Grid item xs={3}>
          {isLinkedToJob ? (
            // When linked to job: Button without NavLink
            <Button
              variant="contained"
              disabled={true}
              sx={{
                '&&': {
                  backgroundColor: 'gray',
                  '&:hover': { backgroundColor: 'gray' },
                },
                marginRight: '50%',
                padding: '12px 40px',
                maxHeight: '45px',
                maxWidth: '96px',
                fontWeight: 'bold',
              }}
            >
              Edit
            </Button>
          ) : (
            // When not linked to job: Button with NavLink
            <NavLink
              to={`/manage-assessments/edit/${assessmentId}`}
              state={{
                title: title,
                description: description,
              }}
            >
              <Button
                variant="contained"
                sx={{
                  '&&': {
                    backgroundColor: 'var(--ttg-green)',
                    '&:hover': { backgroundColor: 'var(--button-hover-green)' },
                  },
                  marginRight: '50%',
                  padding: '12px 40px',
                  maxHeight: '45px',
                  maxWidth: '96px',
                  fontWeight: 'bold',
                }}
              >
                Edit
              </Button>
            </NavLink>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ViewAssessmentHR;
